import axiosInstance from '../config/axiosApi/axiosApi';
import { AUTH_CONFIG } from '../config/authConfig';
import { setAuthCookies, clearAuthCookies, getAuthTokens } from '../utils/cookieUtils';

/**
 * Authentication Service
 * Handles all authentication-related operations
 */
class AuthService {
  /**
   * Login user with credentials
   * @param {string} userName - Username
   * @param {string} password - Password
   * @returns {Promise<Object>} User data and tokens
   */
  async login(userName, password) {
    try {
      const response = await axiosInstance.get(AUTH_CONFIG.ENDPOINTS.LOGIN, {
        params: { userName, password }
      });

      if (response.status === 200 && response.data.status) {
        const { token, userData, refreshToken } = response.data.data;
        
        // Store tokens based on configuration
        if (AUTH_CONFIG.FEATURES.HTTP_ONLY_COOKIES) {
          // Store in secure cookies (when backend supports httpOnly)
          setAuthCookies(token, refreshToken, AUTH_CONFIG.COOKIE_SETTINGS);
        } else {
          // Store in localStorage for now
          localStorage.setItem('user', JSON.stringify({
            token,
            userData,
            refreshToken
          }));
          
          if (refreshToken) {
            localStorage.setItem('refreshToken', refreshToken);
          }
        }

        return {
          success: true,
          data: { token, userData, refreshToken }
        };
      }

      return {
        success: false,
        error: response.data.message || 'Login failed'
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || 'Login failed'
      };
    }
  }

  /**
   * Refresh access token using refresh token
   * @returns {Promise<Object>} New tokens
   */
  async refreshToken() {
    try {
      let refreshToken;
      
      if (AUTH_CONFIG.FEATURES.HTTP_ONLY_COOKIES) {
        const tokens = getAuthTokens();
        refreshToken = tokens.refreshToken;
      } else {
        refreshToken = localStorage.getItem('refreshToken');
      }

      if (!refreshToken) {
        throw new Error('No refresh token available');
      }

      const response = await axiosInstance.post(AUTH_CONFIG.ENDPOINTS.REFRESH, {
        refreshToken
      });

      if (response.data.status) {
        const { token, refreshToken: newRefreshToken } = response.data.data;
        
        // Update stored tokens
        if (AUTH_CONFIG.FEATURES.HTTP_ONLY_COOKIES) {
          setAuthCookies(token, newRefreshToken || refreshToken, AUTH_CONFIG.COOKIE_SETTINGS);
        } else {
          const userData = JSON.parse(sessionStorage.getItem('user') || '{}');
          userData.token = token;
          if (newRefreshToken) {
            userData.refreshToken = newRefreshToken;
            sessionStorage.setItem('refreshToken', newRefreshToken);
          }
          sessionStorage.setItem('user', JSON.stringify(userData));
        }

        return {
          success: true,
          data: { token, refreshToken: newRefreshToken || refreshToken }
        };
      }

      return {
        success: false,
        error: response.data.message || 'Token refresh failed'
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || 'Token refresh failed'
      };
    }
  }

  /**
   * Logout user and clear all tokens
   * @returns {Promise<Object>} Logout result
   */
  async logout() {
    try {
      // Call logout endpoint if available
      if (AUTH_CONFIG.ENDPOINTS.LOGOUT) {
        await axiosInstance.post(AUTH_CONFIG.ENDPOINTS.LOGOUT);
      }
    } catch (error) {
      // Logout endpoint call failed silently
    } finally {
      // Clear all stored tokens regardless of endpoint success
      if (AUTH_CONFIG.FEATURES.HTTP_ONLY_COOKIES) {
        clearAuthCookies();
      } else {
        sessionStorage.removeItem('user');
        sessionStorage.removeItem('refreshToken');
      }
    }

    return { success: true };
  }

  /**
   * Validate current token
   * @returns {Promise<Object>} Validation result
   */
  async validateToken() {
    try {
      const response = await axiosInstance.get(AUTH_CONFIG.ENDPOINTS.VALIDATE);
      return {
        success: true,
        valid: response.data.status,
        data: response.data
      };
    } catch (error) {
      return {
        success: false,
        valid: false,
        error: error.response?.data?.message || 'Token validation failed'
      };
    }
  }

  /**
   * Get current user data
   * @returns {Object|null} User data
   */
  getCurrentUser() {
    if (AUTH_CONFIG.FEATURES.HTTP_ONLY_COOKIES) {
      // When using httpOnly cookies, user data should come from API
      return null;
    } else {
      try {
        const userData = sessionStorage.getItem('user');
        return userData ? JSON.parse(userData) : null;
      } catch (error) {
        return null;
      }
    }
  }

  /**
   * Check if user is authenticated
   * @returns {boolean} Authentication status
   */
  isAuthenticated() {
    const user = this.getCurrentUser();
    return !!(user && user.token);
  }

  /**
   * Get access token
   * @returns {string|null} Access token
   */
  getAccessToken() {
    if (AUTH_CONFIG.FEATURES.HTTP_ONLY_COOKIES) {
      const tokens = getAuthTokens();
      return tokens.accessToken;
    } else {
      const user = this.getCurrentUser();
      return user?.token || null;
    }
  }

  /**
   * Get refresh token
   * @returns {string|null} Refresh token
   */
  getRefreshToken() {
    if (AUTH_CONFIG.FEATURES.HTTP_ONLY_COOKIES) {
      const tokens = getAuthTokens();
      return tokens.refreshToken;
    } else {
      return localStorage.getItem('refreshToken');
    }
  }
}

export default new AuthService();

